import { useEffect, useState } from 'react';

/**
 * A small router. Five routes is not a library's worth of problem:
 *   /              start
 *   /p/:id         proposal (building → workspace)
 *   /print/:id     print view (?print=1 auto-opens the print dialog)
 *   /share/:token  read-only share view (no auth, no app chrome)
 *   /dev/agent-bar  fixture-only AgentBar state lab (no proposal data)
 *   /admin/credits proposal credits admin (one hard-coded admin; docs/limits.md)
 *   /settings      read-only account, org and credits page from the account menu
 */

export type Route =
  | { name: 'start' }
  | { name: 'proposal'; id: string }
  | { name: 'print'; id: string; autoPrint: boolean }
  | { name: 'share'; token: string }
  | { name: 'agentBarDev' }
  | { name: 'adminCredits' }
  | { name: 'settings' };

const NAV_EVENT = 'app:navigate';

export function parseRoute(pathname: string, search: string): Route {
  if (/^\/dev\/agent-bar\/?$/.test(pathname)) return { name: 'agentBarDev' };
  if (/^\/admin\/credits\/?$/.test(pathname)) return { name: 'adminCredits' };
  // The old /settings/account and /settings/org links land on the one page.
  if (/^\/settings(\/(account|org))?\/?$/.test(pathname)) return { name: 'settings' };
  const print = pathname.match(/^\/print\/([^/]+)\/?$/);
  if (print?.[1]) {
    return {
      name: 'print',
      id: decodeURIComponent(print[1]),
      autoPrint: new URLSearchParams(search).get('print') === '1',
    };
  }
  const share = pathname.match(/^\/share\/([^/]+)\/?$/);
  if (share?.[1]) return { name: 'share', token: decodeURIComponent(share[1]) };
  const proposal = pathname.match(/^\/p\/([^/]+)\/?$/);
  if (proposal?.[1]) return { name: 'proposal', id: decodeURIComponent(proposal[1]) };
  return { name: 'start' };
}

/** `replace` swaps the current history entry, so Back doesn't return to a
 * page that just bounced the rep away. */
export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace) window.history.replaceState({}, '', to);
  else window.history.pushState({}, '', to);
  window.dispatchEvent(new Event(NAV_EVENT));
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const update = () => setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener('popstate', update);
    window.addEventListener(NAV_EVENT, update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener(NAV_EVENT, update);
    };
  }, []);

  return route;
}

export const proposalPath = (id: string): string => `/p/${encodeURIComponent(id)}`;
export const printPath = (id: string, autoPrint = false): string =>
  `/print/${encodeURIComponent(id)}${autoPrint ? '?print=1' : ''}`;
